// src/services/backupConfig.js

/**
 * Configuration centralisée du système de sauvegarde
 * Toutes les options configurables sont ici pour faciliter la maintenance
 */

export const BACKUP_CONFIG = {
  // ================================
  // PARAMÈTRES GÉNÉRAUX
  // ================================
  
  /**
   * Version du système de sauvegarde
   */
  VERSION: '1.0.0',

  /**
   * Environnements supportés
   */
  ENVIRONMENTS: {
    DEVELOPMENT: 'development',
    STAGING: 'staging', 
    PRODUCTION: 'production'
  },

  // ================================
  // COLLECTIONS À SAUVEGARDER
  // ================================
  
  /**
   * Collections critiques (données client)
   * ⚠️ PRIORITÉ MAXIMALE - Sauvegarde obligatoire
   */
  CRITICAL_COLLECTIONS: [
    'users',           // Utilisateurs
    'dossiers',        // Dossiers clients  
    'discussions',     // Messagerie
    'notifications',   // Notifications
    'clientUsers',     // Profils clients
    'documents'        // Documents partagés
  ],

  /**
   * Collections système (configuration)
   * ⚙️ PRIORITÉ ÉLEVÉE - Sauvegarde recommandée
   */
  SYSTEM_COLLECTIONS: [
    'settings',        // Paramètres système
    'backups',         // Historique des sauvegardes
    'logs',           // Journaux d'activité
    'restores'        // Historique des restaurations
  ],

  /**
   * Collections optionnelles (analytics, cache)
   * 📊 PRIORITÉ BASSE - Sauvegarde optionnelle
   */
  OPTIONAL_COLLECTIONS: [
    'analytics',       // Données d'analyse
    'cache',          // Cache temporaire
    'sessions'        // Sessions utilisateurs
  ],

  // ================================
  // FRÉQUENCES DE SAUVEGARDE
  // ================================
  
  SCHEDULES: {
    /**
     * Sauvegarde automatique (en millisecondes)
     */
    AUTO_BACKUP_INTERVAL: {
      DEVELOPMENT: 2 * 60 * 60 * 1000,    // 2 heures
      STAGING: 1 * 60 * 60 * 1000,        // 1 heure  
      PRODUCTION: 30 * 60 * 1000           // 30 minutes
    },

    /**
     * Sauvegarde complète quotidienne (heure)
     */
    DAILY_BACKUP_HOUR: {
      DEVELOPMENT: 23,   // 23h
      STAGING: 2,        // 2h du matin
      PRODUCTION: 3      // 3h du matin
    },

    /**
     * Sauvegarde hebdomadaire (jour de la semaine)
     * 0 = Dimanche, 1 = Lundi, etc.
     */
    WEEKLY_BACKUP_DAY: 0, // Dimanche

    /**
     * Nettoyage automatique (jours)
     */
    CLEANUP_INTERVAL: 7   // Tous les 7 jours
  },

  // ================================
  // POLITIQUE DE RÉTENTION
  // ================================
  
  RETENTION: {
    /**
     * Sauvegardes horaires
     */
    HOURLY: {
      keep: 48,           // Garder 48h (2 jours)
      description: '2 derniers jours'
    },

    /**
     * Sauvegardes quotidiennes  
     */
    DAILY: {
      keep: 30,           // Garder 30 jours
      description: '30 derniers jours'
    },

    /**
     * Sauvegardes hebdomadaires
     */
    WEEKLY: {
      keep: 12,           // Garder 12 semaines (3 mois)
      description: '3 derniers mois'
    },

    /**
     * Sauvegardes mensuelles
     */
    MONTHLY: {
      keep: 12,           // Garder 12 mois (1 an)
      description: '12 derniers mois'
    },

    /**
     * Sauvegardes annuelles (archivage)
     */
    YEARLY: {
      keep: 7,            // Garder 7 ans
      description: '7 dernières années'
    }
  },

  // ================================
  // LIMITES TECHNIQUES
  // ================================
  
  LIMITS: {
    /**
     * Taille maximale d'un backup (bytes)
     */
    MAX_BACKUP_SIZE: 500 * 1024 * 1024,    // 500 MB

    /**
     * Nombre max de documents par batch Firestore
     */
    FIRESTORE_BATCH_SIZE: 500,

    /**
     * Timeout pour une opération (ms)
     */
    OPERATION_TIMEOUT: 10 * 60 * 1000,     // 10 minutes

    /**
     * Nombre max de sauvegardes simultanées
     */
    MAX_CONCURRENT_BACKUPS: 1,

    /**
     * Nombre max de tentatives en cas d'échec
     */
    MAX_RETRY_ATTEMPTS: 3,

    /**
     * Délai entre les tentatives (ms)
     */
    RETRY_DELAY: 5000                       // 5 secondes
  },

  // ================================
  // NOTIFICATIONS & ALERTES
  // ================================
  
  NOTIFICATIONS: {
    /**
     * Types d'événements à notifier
     */
    EVENTS: {
      BACKUP_SUCCESS: 'backup_success',
      BACKUP_FAILURE: 'backup_failure', 
      BACKUP_WARNING: 'backup_warning',
      RESTORE_SUCCESS: 'restore_success',
      RESTORE_FAILURE: 'restore_failure',
      STORAGE_FULL: 'storage_full',
      RETENTION_CLEANUP: 'retention_cleanup'
    },

    /**
     * Canaux de notification
     */
    CHANNELS: {
      EMAIL: 'email',
      IN_APP: 'in_app',
      WEBHOOK: 'webhook'
    },

    /**
     * Configuration par défaut
     */
    DEFAULT_SETTINGS: {
      backup_success: ['in_app'],
      backup_failure: ['email', 'in_app'],
      restore_success: ['in_app'],
      restore_failure: ['email', 'in_app'],
      storage_full: ['email', 'in_app']
    }
  },

  // ================================
  // SÉCURITÉ
  // ================================
  
  SECURITY: {
    /**
     * Rôles autorisés pour les opérations
     */
    PERMISSIONS: {
      CREATE_BACKUP: ['superadmin'],
      RESTORE_BACKUP: ['superadmin'],
      DELETE_BACKUP: ['superadmin'],  
      VIEW_BACKUPS: ['superadmin', 'admin'],
      CONFIGURE_SYSTEM: ['superadmin']
    },

    /**
     * Chiffrement des sauvegardes
     */
    ENCRYPTION: {
      enabled: true,
      algorithm: 'AES-256-GCM'
    },

    /**
     * Audit trail
     */
    AUDIT: {
      log_all_operations: true,
      retain_logs_days: 365
    }
  },

  // ================================
  // PERFORMANCE
  // ================================
  
  PERFORMANCE: {
    /**
     * Optimisations
     */
    COMPRESSION: {
      enabled: true,
      level: 6          // Niveau de compression (1-9)
    },

    /**
     * Traitement parallèle
     */
    PARALLEL: {
      enabled: true,
      max_workers: 4    // Nombre max de workers parallèles
    },

    /**
     * Cache
     */
    CACHE: {
      enabled: true,
      ttl: 60 * 5      // 5 minutes
    }
  },

  // ================================
  // MONITORING
  // ================================
  
  MONITORING: {
    /**
     * Métriques à collecter
     */
    METRICS: [
      'backup_duration',
      'backup_size', 
      'documents_count',
      'success_rate',
      'storage_usage',
      'restore_time'
    ],

    /**
     * Seuils d'alerte
     */
    THRESHOLDS: {
      backup_duration_max: 30 * 60 * 1000,  // 30 minutes
      success_rate_min: 0.95,                // 95%
      storage_usage_max: 0.85                // 85%
    }
  }
};

/**
 * Configuration dynamique par environnement
 */
export const getEnvironmentConfig = () => {
  const env = process.env.NODE_ENV || 'development';
  
  return {
    environment: env,
    autoBackupInterval: BACKUP_CONFIG.SCHEDULES.AUTO_BACKUP_INTERVAL[env.toUpperCase()] || 
                       BACKUP_CONFIG.SCHEDULES.AUTO_BACKUP_INTERVAL.DEVELOPMENT,
    dailyBackupHour: BACKUP_CONFIG.SCHEDULES.DAILY_BACKUP_HOUR[env.toUpperCase()] || 
                     BACKUP_CONFIG.SCHEDULES.DAILY_BACKUP_HOUR.DEVELOPMENT,
    isProduction: env === 'production',
    isDevelopment: env === 'development'
  };
};

/**
 * Configuration par défaut pour une nouvelle installation
 */
export const DEFAULT_USER_CONFIG = {
  autoBackup: true,
  backupFrequency: 'hourly',
  retentionDays: 30,
  notifications: {
    email: true,
    inApp: true
  },
  includeOptionalCollections: false,
  compressionEnabled: true
};

/**
 * Validation de la configuration
 */
export const validateConfig = (userConfig) => {
  const errors = [];
  
  // Vérifier la fréquence
  const validFrequencies = ['hourly', 'daily', 'weekly', 'manual'];
  if (!validFrequencies.includes(userConfig.backupFrequency)) {
    errors.push(`Fréquence invalide: ${userConfig.backupFrequency}`);
  }
  
  // Vérifier la rétention
  if (userConfig.retentionDays < 1 || userConfig.retentionDays > 365) {
    errors.push('La rétention doit être entre 1 et 365 jours');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Fusionner la config utilisateur avec les défauts
 */
export const mergeUserConfig = (userConfig = {}) => {
  return {
    ...DEFAULT_USER_CONFIG,
    ...userConfig,
    // S'assurer que les notifications ont une structure valide
    notifications: {
      ...DEFAULT_USER_CONFIG.notifications,
      ...(userConfig.notifications || {})
    }
  };
};

export default BACKUP_CONFIG;